import React from 'react';
import { useNavigate } from 'react-router-dom';

const CustomerSupport = () => {
  const navigate = useNavigate();

  // ── Session validation ──
  React.useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (!userStr) {
      navigate("/login");
      return;
    }
    try {
      const userObj = JSON.parse(userStr);
      if (userObj && userObj.exp) {
        const currentTime = Date.now() / 1000;
        if (userObj.exp < currentTime) {
          localStorage.removeItem("user");
          localStorage.removeItem("userMobile");
          navigate("/login");
        }
      }
    } catch (e) {
      localStorage.removeItem("user");
      localStorage.removeItem("userMobile");
      navigate("/login");
    }
  }, [navigate]);

  // ── Form state ──
  const [mobile, setMobile] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [details, setDetails] = React.useState('');
  const [errors, setErrors] = React.useState({});
  const [submitted, setSubmitted] = React.useState(false);
  const [isMobile, setIsMobileView] = React.useState(window.innerWidth < 768);
  const [topic, setTopic] = React.useState('');

  React.useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!/^\d{10}$/.test(mobile)) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    if (!topic) {
      newErrors.topic = 'Please select a topic';
    }
    if (!subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (subject.length > 20) {
      newErrors.subject = 'Subject cannot exceed 20 characters';
    }
    if (!details.trim()) {
      newErrors.details = 'Ticket details are required';
    } else if (details.length > 200) {
      newErrors.details = 'Details cannot exceed 200 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      // TODO: send ticket to backend API
      console.log('Ticket submitted:', { mobile, topic, subject, details });
      setSubmitted(true);
    }
  };

  const handleMobileChange = (e) => {
    const val = e.target.value.replace(/\D/g, ''); // digits only
    if (val.length <= 10) setMobile(val);
  };

  // ── Shared styles ──
  const labelStyle = {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#37474f',
    marginBottom: '6px',
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    fontSize: '0.95rem',
    border: '1.5px solid #cfd8dc',
    borderRadius: '10px',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
  };

  const inputFocusHandler = (e) => {
    e.currentTarget.style.borderColor = '#2196f3';
    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(33,150,243,0.12)';
  };
  const inputBlurHandler = (e) => {
    e.currentTarget.style.borderColor = '#cfd8dc';
    e.currentTarget.style.boxShadow = 'none';
  };

  const errorStyle = {
    color: '#e53935',
    fontSize: '0.78rem',
    marginTop: '4px',
    fontWeight: '500',
  };

  const counterStyle = (current, max) => ({
    textAlign: 'right',
    fontSize: '0.72rem',
    color: current > max ? '#e53935' : '#90a4ae',
    marginTop: '3px',
    fontWeight: '500',
  });

  // ── Success view ──
  if (submitted) {
    return (
      <div style={{
        fontFamily: 'Inter, sans-serif',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
        padding: '20px',
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(14px)',
          borderRadius: '20px',
          padding: isMobile ? '36px 24px' : '50px 48px',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          border: '1px solid rgba(255,255,255,0.6)',
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '16px' }}>✅</div>
          <h2 style={{
            color: '#2e7d32',
            fontSize: '1.5rem',
            fontWeight: '700',
            marginTop: 0,
            marginBottom: '12px',
          }}>Ticket Submitted!</h2>
          <p style={{
            color: '#546e7a',
            fontSize: '0.95rem',
            lineHeight: '1.6',
            marginBottom: '28px',
          }}>
            Your support ticket has been submitted successfully.
            Our team will get back to you shortly.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                setMobile('');
                setSubject('');
                setDetails('');
                setTopic('');
                setErrors({});
                setSubmitted(false);
              }}
              style={{
                padding: '10px 24px',
                backgroundColor: '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s, transform 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1976d2';
                e.currentTarget.style.transform = 'scale(1.03)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2196f3';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >Submit Another</button>
            <button
              onClick={() => navigate('/utility-dashboard')}
              style={{
                padding: '10px 24px',
                backgroundColor: 'transparent',
                color: '#546e7a',
                border: '1.5px solid #cfd8dc',
                borderRadius: '10px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#2196f3';
                e.currentTarget.style.color = '#2196f3';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#cfd8dc';
                e.currentTarget.style.color = '#546e7a';
              }}
            >Back to Home</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form view ──
  return (
    <div style={{
      fontFamily: 'Inter, sans-serif',
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
      padding: '20px',
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(14px)',
        borderRadius: '20px',
        padding: isMobile ? '32px 22px' : '44px 44px',
        maxWidth: '520px',
        width: '100%',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        border: '1px solid rgba(255,255,255,0.6)',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ fontSize: '2.8rem', marginBottom: '8px' }}>🎧</div>
          <h2 style={{
            margin: 0,
            fontSize: isMobile ? '1.4rem' : '1.6rem',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #2196f3, #1565c0)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>Customer Support</h2>
          <p style={{
            color: '#78909c',
            fontSize: '0.88rem',
            marginTop: '6px',
            marginBottom: 0,
          }}>Submit a ticket and we'll get back to you</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {/* Mobile Number */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Mobile Number</label>
            <input
              type="tel"
              value={mobile}
              onChange={handleMobileChange}
              placeholder="Enter 10-digit mobile number"
              style={{
                ...inputStyle,
                ...(errors.mobile ? { borderColor: '#e53935' } : {}),
              }}
              onFocus={inputFocusHandler}
              onBlur={inputBlurHandler}
              inputMode="numeric"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {errors.mobile
                ? <span style={errorStyle}>{errors.mobile}</span>
                : <span />
              }
              <span style={counterStyle(mobile.length, 10)}>{mobile.length}/10</span>
            </div>
          </div>

          {/* Topic */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Topic</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
              {[
                { label: 'Rent', emoji: '🏠', color: '#e65100', bg: 'linear-gradient(135deg, #fff3e0, #ffe0b2)', border: '#ff9800', shadow: 'rgba(255,152,0,0.25)' },
                { label: 'Flat-Buy', emoji: '🏢', color: '#1b5e20', bg: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)', border: '#4caf50', shadow: 'rgba(76,175,80,0.25)' },
                { label: 'Flat-Sale', emoji: '🏷️', color: '#4a148c', bg: 'linear-gradient(135deg, #f3e5f5, #e1bee7)', border: '#9c27b0', shadow: 'rgba(156,39,176,0.25)' },
                { label: 'TrustApp queries', emoji: '💬', color: '#0d47a1', bg: 'linear-gradient(135deg, #e3f2fd, #bbdefb)', border: '#2196f3', shadow: 'rgba(33,150,243,0.25)' },
              ].map(({ label, emoji, color, bg, border, shadow }) => {
                const isSelected = topic === label;
                return (
                  <label
                    key={label}
                    onClick={() => setTopic(label)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: `2px solid ${isSelected ? border : '#e0e0e0'}`,
                      background: isSelected ? bg : '#fafafa',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      fontSize: '0.88rem',
                      fontWeight: isSelected ? '700' : '500',
                      color: isSelected ? color : '#78909c',
                      userSelect: 'none',
                      boxShadow: isSelected ? `0 4px 14px ${shadow}` : '0 1px 3px rgba(0,0,0,0.04)',
                      transform: isSelected ? 'scale(1.03)' : 'scale(1)',
                    }}
                  >
                    <span style={{
                      fontSize: '1.3rem',
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: isSelected ? `${border}22` : '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.25s ease',
                      flexShrink: 0,
                    }}>{emoji}</span>
                    <span style={{ flex: 1 }}>{label}</span>
                    <span style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: `2px solid ${isSelected ? border : '#bdbdbd'}`,
                      background: isSelected ? border : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.25s ease',
                      flexShrink: 0,
                    }}>
                      {isSelected && (
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
            {errors.topic && <span style={{ ...errorStyle, display: 'block', marginTop: '6px' }}>{errors.topic}</span>}
          </div>

          {/* Subject */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => {
                if (e.target.value.length <= 20) setSubject(e.target.value);
              }}
              placeholder="Brief subject"
              style={{
                ...inputStyle,
                ...(errors.subject ? { borderColor: '#e53935' } : {}),
              }}
              onFocus={inputFocusHandler}
              onBlur={inputBlurHandler}
              maxLength={20}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {errors.subject
                ? <span style={errorStyle}>{errors.subject}</span>
                : <span />
              }
              <span style={counterStyle(subject.length, 20)}>{subject.length}/20</span>
            </div>
          </div>

          {/* Ticket Details */}
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Ticket Details</label>
            <textarea
              value={details}
              onChange={(e) => {
                if (e.target.value.length <= 200) setDetails(e.target.value);
              }}
              placeholder="Describe in detail..."
              rows={4}
              style={{
                ...inputStyle,
                resize: 'vertical',
                minHeight: '100px',
                ...(errors.details ? { borderColor: '#e53935' } : {}),
              }}
              onFocus={inputFocusHandler}
              onBlur={inputBlurHandler}
              maxLength={200}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {errors.details
                ? <span style={errorStyle}>{errors.details}</span>
                : <span />
              }
              <span style={counterStyle(details.length, 200)}>{details.length}/200</span>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              type="submit"
              style={{
                flex: 1,
                minWidth: '140px',
                padding: '13px 20px',
                background: 'linear-gradient(135deg, #2196f3, #1565c0)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.2s',
                boxShadow: '0 4px 14px rgba(33,150,243,0.35)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(33,150,243,0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(33,150,243,0.35)';
              }}
            >Submit Ticket</button>
            <button
              type="button"
              onClick={() => navigate('/utility-dashboard')}
              style={{
                flex: 1,
                minWidth: '140px',
                padding: '13px 20px',
                backgroundColor: 'transparent',
                color: '#546e7a',
                border: '1.5px solid #cfd8dc',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#2196f3';
                e.currentTarget.style.color = '#2196f3';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#cfd8dc';
                e.currentTarget.style.color = '#546e7a';
              }}
            >Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerSupport;
